import { useKeycloak } from "@react-keycloak/web";
import { useMemo } from "react";

/**
 * Personal claim names expected from Keycloak OIDC token mappers (camelCase).
 * Order is stable for validation and docs.
 */
export const OIDC_PERSONAL_CLAIM_KEYS = [
  "username",
  "email",
  "firstName",
  "lastName",
  "mobilePhone",
  "address",
];

export function firstString(...candidates) {
  for (const v of candidates) {
    if (v === null || v === undefined) continue;
    const s = typeof v === "string" ? v.trim() : String(v).trim();
    if (s) return s;
  }
  return null;
}

/** OIDC `address` claim can be an object; Keycloak may also store a plain string. */
export function formatAddressFromToken(addr) {
  if (addr === null || addr === undefined) return null;
  if (typeof addr === "string") {
    const s = addr.trim();
    return s || null;
  }
  if (typeof addr === "object") {
    if (typeof addr.formatted === "string" && addr.formatted.trim()) {
      return addr.formatted.trim();
    }
    const parts = [
      addr.street_address,
      addr.locality,
      addr.region,
      addr.postal_code,
      addr.country,
    ]
      .filter((p) => typeof p === "string" && p.trim())
      .map((p) => p.trim());
    return parts.length ? parts.join(", ") : null;
  }
  return null;
}

/**
 * Reads personal fields from the access token using project OIDC names first,
 * then common standard/fallback claim names.
 */
export function resolvePersonalClaimsFromToken(tokenParsed) {
  if (!tokenParsed || typeof tokenParsed !== "object") {
    return {
      username: null,
      email: null,
      firstName: null,
      lastName: null,
      mobilePhone: null,
      address: null,
    };
  }

  const address =
    formatAddressFromToken(tokenParsed.address) ??
    firstString(
      tokenParsed.street_address,
      tokenParsed.streetAddress,
      tokenParsed.user_address
    );

  return {
    username: firstString(tokenParsed.username, tokenParsed.preferred_username),
    email: firstString(tokenParsed.email),
    firstName: firstString(
      tokenParsed.firstName,
      tokenParsed.given_name,
      tokenParsed.first_name
    ),
    lastName: firstString(
      tokenParsed.lastName,
      tokenParsed.family_name,
      tokenParsed.last_name
    ),
    mobilePhone: firstString(
      tokenParsed.mobilePhone,
      tokenParsed.phone_number,
      tokenParsed.phone,
      tokenParsed.mobile_phone,
      tokenParsed.telephone
    ),
    address,
  };
}

/**
 * Per expected claim: whether the raw token carries a non-empty value under the canonical key.
 * Use in tests or dev tooling — does not apply fallback claim names.
 */
export function validateCanonicalOidcPersonalClaims(tokenParsed) {
  /** @type {Record<string, "ok" | "missing" | "empty">} */
  const details = {};

  for (const key of OIDC_PERSONAL_CLAIM_KEYS) {
    if (!tokenParsed || !Object.hasOwn(tokenParsed, key)) {
      details[key] = "missing";
      continue;
    }
    const raw = tokenParsed[key];
    let ok = false;
    if (key === "address") {
      ok =
        formatAddressFromToken(raw) != null ||
        (typeof raw === "string" && raw.trim().length > 0);
    } else if (typeof raw === "string") {
      ok = raw.trim().length > 0;
    } else if (raw != null && typeof raw === "object") {
      ok = Object.keys(raw).length > 0;
    } else {
      ok = true;
    }
    details[key] = ok ? "ok" : "empty";
  }

  return {
    details,
    allCanonicalPresentAndFilled: Object.values(details).every(
      (s) => s === "ok"
    ),
  };
}

/** Keycloak system / default roles hidden from the profile "Assigned roles" UI. */
const PROFILE_HIDDEN_ROLES_EXACT = new Set([
  "offline_access",
  "default-roles-r3mydas-marketplace",
  "uma_authorization",
]);

export function isProfileDisplayRole(role) {
  if (typeof role !== "string") return false;
  const normalized = role.trim();
  if (!normalized) return false;
  if (PROFILE_HIDDEN_ROLES_EXACT.has(normalized)) return false;
  if (normalized.toLowerCase().startsWith("uma")) return false;
  return true;
}

export function filterProfileDisplayRoles(roles) {
  return (roles ?? []).filter(isProfileDisplayRole);
}

function capitalizeDisplayName(value) {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * Friendly name for profile greetings (e.g. "Hi Alex").
 * @param {{ firstName?: string|null, preferredUsername?: string|null, email?: string|null }} identity
 */
export function resolveProfileDisplayName({
  firstName,
  preferredUsername,
  email,
}) {
  if (firstName?.trim()) {
    return capitalizeDisplayName(firstName.trim());
  }
  if (preferredUsername?.trim()) {
    return capitalizeDisplayName(preferredUsername.trim());
  }
  if (email?.trim()) {
    const local = email.trim().split("@")[0];
    if (local) return capitalizeDisplayName(local);
  }
  return null;
}

/**
 * Identity and roles from Keycloak only (no marketplace `/user` entity).
 */
export function useMarketplaceIdentity() {
  const { keycloak } = useKeycloak();
  const isAuthenticated = !!keycloak?.authenticated;
  const tokenParsed = keycloak?.tokenParsed;

  return useMemo(() => {
    const roles = tokenParsed?.realm_access?.roles ?? [];
    const realmRole = (name) =>
      typeof keycloak?.hasRealmRole === "function"
        ? keycloak.hasRealmRole(name)
        : roles.includes(name);

    const legacyRole = tokenParsed?.userRole;
    const showSellerNav =
      ["hybrid", "seller"].includes(legacyRole) ||
      realmRole("seller") ||
      realmRole("hybrid") ||
      roles.some((r) => /^(seller|hybrid)$/i.test(r));

    const personal = resolvePersonalClaimsFromToken(tokenParsed);

    const profile = {
      preferredUsername: personal.username,
      email: personal.email,
      firstName: personal.firstName,
      lastName: personal.lastName,
    };

    return {
      sub: tokenParsed?.sub ?? null,
      /** Login / display handle: `username` claim, then Keycloak `preferred_username`. */
      preferredUsername: profile.preferredUsername,
      email: profile.email,
      firstName: profile.firstName,
      lastName: profile.lastName,
      mobilePhone: personal.mobilePhone,
      address: personal.address,
      displayName: resolveProfileDisplayName(profile),
      realmRoles: roles,
      realmRole,
      showSellerNav,
      isAuthenticated,
    };
  }, [keycloak, isAuthenticated, tokenParsed]);
}
